export type PriceList = {
  price?: number;
  identifier?: string;
  startDate?: string;
  endDate?: string;
  modifier?: number;
  modifierType?: string;
};

export type Price = {
  current: number | null;
  previous: number | null;
  hasOffer: boolean | null;
  subjectToVAT?: boolean | null;
  totalWithVAT?: number | null;
};

function toTimestamp(value?: string) {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function isPriceListActive(
  priceList: PriceList,
  currentTimestamp: number,
): boolean {
  const startTimestamp = toTimestamp(priceList.startDate);
  if (startTimestamp != null && currentTimestamp < startTimestamp) return false;

  const endTimestamp = toTimestamp(priceList.endDate);
  if (endTimestamp != null && currentTimestamp > endTimestamp) return false;

  return true;
}

/**
 * Calculate amount including VAT if needed.
 * Returns the original amount if it is 0 or falsy.
 * Returns amount * 1.25 if subjectToVAT is true (assuming amount excludes 25% VAT).
 * @param amount Amount excluding VAT when subjectToVAT is true.
 * @param subjectToVAT Whether the amount is subject to VAT and should be converted to an amount including VAT.
 */
function amountWithoutVAT(
  amount: number | null | undefined,
  subjectToVAT: boolean | null | undefined,
) {
  if (!amount || amount === 0) return amount;
  return subjectToVAT ? amount * 1.25 : amount;
}

export function getPriceOffer({
  originalItemPrice,
  priceLists,
  subjectToVAT,
  total: total,
  quantity = 1,
}: {
  originalItemPrice?: number | null;
  quantity?: number;
  subjectToVAT?: boolean | null | undefined;
  total?: number | null;
  priceLists?: PriceList[] | null;
}): Price {
  if (!originalItemPrice)
    return { current: null, previous: null, hasOffer: false };
  const originalItemTotal = originalItemPrice * quantity;
  const currentTimestamp = Date.now();
  const safePriceLists = Array.isArray(priceLists) ? priceLists : [];

  if (!originalItemTotal)
    return { current: null, previous: null, hasOffer: false };

  const activeOfferPrices =
    safePriceLists
      ?.filter((priceList) => isPriceListActive(priceList, currentTimestamp))
      .map(({ price }) => price)
      .filter(
        (price): price is number =>
          typeof price === "number" && Number.isFinite(price),
      ) ?? [];

  const offerItemPrice =
    activeOfferPrices.length > 0 ? Math.min(...activeOfferPrices) : null;

  const validOffer =
    offerItemPrice != null && offerItemPrice < originalItemPrice
      ? offerItemPrice * quantity
      : null;

  const effectiveTotal = total ?? validOffer ?? originalItemTotal;

  const totalWithVAT = amountWithoutVAT(effectiveTotal, subjectToVAT);
  if (total || total === 0) {
    const hasOffer = !!(originalItemTotal && total < originalItemTotal);
    const previous = hasOffer ? originalItemTotal : null;
    return {
      current: effectiveTotal,
      previous,
      hasOffer,
      subjectToVAT,
      totalWithVAT,
    };
  }

  const offerTotal =
    offerItemPrice || offerItemPrice === 0 ? offerItemPrice * quantity : null;

  if ((offerTotal || offerTotal === 0) && offerTotal < originalItemTotal)
    return {
      current: offerTotal,
      previous: originalItemTotal,
      hasOffer: true,
      subjectToVAT,
      totalWithVAT,
    };

  return {
    current: originalItemTotal,
    previous: null,
    hasOffer: false,
    subjectToVAT,
    totalWithVAT,
  };
}

export function getCartItemPrice({
  net,
  compareAt,
  quantity,
  subjectToVAT,
  itemLineTotal,
}: {
  net: number | null;
  compareAt: number | null;
  quantity: number | null;
  subjectToVAT?: boolean | null;
  itemLineTotal?: number | null;
}): Price {
  const effectiveNet =
    itemLineTotal != null && quantity ? itemLineTotal / quantity : net;
  const current =
    effectiveNet != null && quantity ? effectiveNet * quantity : effectiveNet;
  const previous = compareAt && quantity ? compareAt * quantity : null;
  const totalWithVAT = amountWithoutVAT(current, subjectToVAT);

  return {
    current,
    previous,
    hasOffer:
      previous != null && totalWithVAT != null && totalWithVAT < previous,
    totalWithVAT,
    subjectToVAT,
  };
}

export type MemberPriceOffer = {
  /** The member price, VAT included */
  memberPrice: number;
  /** The price it is compared with, VAT included */
  comparisonPrice: number;
  /** The comparison price is a campaign open to everyone, not the ordinary price */
  comparisonIsCampaign: boolean;
  /** The user already gets the member price */
  isMember: boolean;
};

/**
 * The member price to show for a product, or null when there is none.
 *
 * A member whose price comes from a member pricelist sees it compared with the
 * ordinary price. Anyone else sees the price of a pricelist they do not get
 * (a membership price) compared with what they pay today, when it is lower.
 */
export function getMemberPriceOffer({
  originalItemPrice,
  subjectToVAT,
  priceLists,
  applicableIdentifiers,
  memberIdentifiers,
}: {
  originalItemPrice?: number | null;
  subjectToVAT?: boolean | null;
  priceLists?: PriceList[] | null;
  /** The pricelists the user gets: open to everyone or to their customer groups */
  applicableIdentifiers: ReadonlySet<string>;
  /** The applicable pricelists that come from a customer group */
  memberIdentifiers: ReadonlySet<string>;
}): MemberPriceOffer | null {
  if (!originalItemPrice) return null;

  const safePriceLists = Array.isArray(priceLists) ? priceLists : [];
  const isApplicable = ({ identifier }: PriceList) =>
    applicableIdentifiers.has(identifier ?? "");
  const priceWith = (lists: PriceList[]) => {
    const offer = getPriceOffer({
      originalItemPrice,
      subjectToVAT,
      priceLists: lists,
    });
    return offer.totalWithVAT ?? offer.current;
  };

  const ordinaryPrice = priceWith([]);
  const currentPrice = priceWith(safePriceLists.filter(isApplicable));
  if (ordinaryPrice == null || currentPrice == null) return null;

  const toOffer = (
    memberPrice: number,
    comparisonPrice: number,
    isMember: boolean,
  ): MemberPriceOffer => ({
    memberPrice,
    comparisonPrice,
    comparisonIsCampaign: comparisonPrice < ordinaryPrice,
    isMember,
  });

  const appliedMemberLists = safePriceLists.filter(
    (list) =>
      isApplicable(list) && memberIdentifiers.has(list.identifier ?? ""),
  );
  if (
    appliedMemberLists.length > 0 &&
    currentPrice < ordinaryPrice &&
    priceWith(appliedMemberLists) === currentPrice
  )
    return toOffer(currentPrice, ordinaryPrice, true);

  const memberOnlyLists = safePriceLists.filter((list) => !isApplicable(list));
  if (memberOnlyLists.length === 0) return null;

  const memberPrice = priceWith(memberOnlyLists);
  if (memberPrice == null || memberPrice >= currentPrice) return null;

  return toOffer(memberPrice, currentPrice, false);
}
